import api from './api';

export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  username?: string;
  role: 'SUPER_ADMIN' | 'CLIENT_ADMIN' | 'AGENT' | 'END_USER';
  company: {
    _id: string;
    name: string;
    type: string;
  };
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  phone?: string;
  timezone: string;
  timezoneAuto?: boolean;
  language: string;
  avatar?: string;
  lastLogin?: string;
  emailVerified?: boolean;
  createdAt: string;
  updatedAt: string;
  allowedCompanies?: Array<{ _id: string; name: string }> | string[];
  canViewAllCompanies?: boolean;
  canViewInvoices?: boolean;
  canViewDeals?: boolean;
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  username?: string;
  role: 'SUPER_ADMIN' | 'CLIENT_ADMIN' | 'AGENT' | 'END_USER';
  company?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  phone?: string;
  timezone: string;
  language: string;
  allowedCompanies?: string[];
  canViewAllCompanies?: boolean;
  canViewInvoices?: boolean;
  canViewDeals?: boolean;
}

export const userService = {
  async getUsers(filters?: {
    company?: string;
    role?: string;
    status?: string;
  }): Promise<User[]> {
    const params = new URLSearchParams();
    if (filters?.company) params.append('company', filters.company);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    
    const response = await api.get(`/users?${params.toString()}`);
    return response.data.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  },

  async createUser(data: CreateUserData): Promise<User> {
    const response = await api.post('/users', data);
    return response.data.data;
  },

  async updateUser(id: string, data: Partial<CreateUserData>): Promise<User> {
    const response = await api.put(`/users/${id}`, data);
    return response.data.data;
  },

  async updateUsername(id: string, username: string): Promise<User> {
    const response = await api.put(`/users/${id}/username`, { username });
    return response.data.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async resetUserPassword(id: string, password: string): Promise<void> {
    await api.post(`/users/${id}/reset-password`, { password });
  },

  async resendVerification(id: string): Promise<void> {
    await api.post(`/users/${id}/resend-verification`);
  },

  async getUsersByCompany(companyId: string): Promise<User[]> {
    const response = await api.get(`/users/company/${companyId}`);
    return response.data.data;
  }
};
